import React, { useState, useEffect, useRef } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "../../services/toast";
import { head, omit } from "lodash";

import { makeStyles } from 'tss-react/mui';
import { green } from "@mui/material/colors";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import CircularProgress from "@mui/material/CircularProgress";

import { i18n } from "../../translate/i18n";
import { isPromptOfType, promptOptionLabel, promptsOfType } from "../../helpers/promptTypes";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import ColorPicker from "../ColorPicker";
import {
  Box,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { AttachFile, Colorize, DeleteOutlined, ThumbsUpDownOutlined } from "@mui/icons-material";
import QueueFlowBuilder from "../QueueFlowBuilder";
import WeeklyScheduleTable, { toWeek, weekHasErrors } from "../WeeklyScheduleTable";
import ConfirmationModal from "../ConfirmationModal";
import SatisfactionSurveyModal from "../SatisfactionSurveyModal";
import QueueAssignmentTab from "../QueueAssignmentTab";

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },

  btnWrapper: {
    position: "relative",
  },

  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  colorAdorment: {
    width: 20,
    height: 20,
  },
  dialogPaper: {
    height: "90vh",
  },
  tabPanel: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    overflow: "auto",
  },
  tabForm: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
  },
  flowColumn: {
    display: "flex",
    flexDirection: "column",
  },
  flowPlaceholder: {
    flex: 1,
    minHeight: 560,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    padding: theme.spacing(3),
    color: theme.palette.text.secondary,
    fontSize: 13,
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: 12,
    background: theme.palette.mode === "light" ? "#f8f9fb" : "#20252c",
  },
  formPanel: {
    minHeight: 560,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.75),
    padding: theme.spacing(2, 2.5, 2.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    background: theme.palette.mode === "light" ? "#fafbfc" : "#20252c",
  },
  sectionTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: 0.8,
    textTransform: "uppercase",
    color: theme.palette.text.disabled,
    marginTop: theme.spacing(0.75),
    "&:first-of-type": {
      marginTop: 0,
    },
    "&::after": {
      content: '""',
      flex: 1,
      height: 1,
      background: theme.palette.divider,
    },
  },
  fieldRow: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
    gap: theme.spacing(1.5),
  },
  attachmentRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 1, 0.5, 1.5),
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
  },
  attachmentName: {
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontSize: "0.86rem",
  },
  surveyRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1, 1.5),
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
  },
}));

const QueueSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, "Too Short!")
    .max(50, "Too Long!")
    .required("Required"),
  color: Yup.string().min(3, "Too Short!").max(9, "Too Long!").required(),
  greetingMessage: Yup.string(),
});

const DEFAULT_ASSIGNMENT_SETTINGS = {
  autoAssignEnabled: false,
  autoAssignOffline: false,
  redistributeUnavailable: false,
};

const QueueModal = ({ open, onClose, queueId }) => {
  const { classes } = useStyles();

  const initialState = {
    name: "",
    color: "",
    greetingMessage: "",
    outOfHoursMessage: "",
    orderQueue: "",
    integrationId: "",
    promptId: "",
    queueGroupId: "",
    ratingEnabled: true
  };

  const [colorPickerModalOpen, setColorPickerModalOpen] = useState(false);
  const [queue, setQueue] = useState(initialState);
  const [tab, setTab] = useState(0);
  const [schedulesEnabled, setSchedulesEnabled] = useState(false);
  const [ratingByQueueEnabled, setRatingByQueueEnabled] = useState(false);
  const [queueSurvey, setQueueSurvey] = useState(null);
  const [surveyModalOpen, setSurveyModalOpen] = useState(false);
  const greetingRef = useRef();
  const [integrations, setIntegrations] = useState([]);
  const [queueGroups, setQueueGroups] = useState([]);
  const [attachment, setAttachment] = useState(null);
  const attachmentFile = useRef(null);
  const [queueEditable, setQueueEditable] = useState(true);
  const [confirmationOpen, setConfirmationOpen] = useState(false);

  const [schedules, setSchedules] = useState([
    { weekday: "Lunes", weekdayEn: "monday", startTime: "08:00", endTime: "18:00", },
    { weekday: "Martes", weekdayEn: "tuesday", startTime: "08:00", endTime: "18:00", },
    { weekday: "Miercoles", weekdayEn: "wednesday", startTime: "08:00", endTime: "18:00", },
    { weekday: "Jueves", weekdayEn: "thursday", startTime: "08:00", endTime: "18:00", },
    { weekday: "Viernes", weekdayEn: "friday", startTime: "08:00", endTime: "18:00", },
    { weekday: "Sabado", weekdayEn: "saturday", startTime: "08:00", endTime: "12:00", },
    { weekday: "Domingo", weekdayEn: "sunday", startTime: "00:00", endTime: "00:00", },
  ]);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [prompts, setPrompts] = useState([]);
  const [botMode, setBotMode] = useState("");
  const [assignmentSettings, setAssignmentSettings] = useState(DEFAULT_ASSIGNMENT_SETTINGS);
  const [agents, setAgents] = useState([]);
  const [agentsLoaded, setAgentsLoaded] = useState(false);

  useEffect(() => {
    if (!open) return;
    setAgentsLoaded(false);
    api
      .get(`/queue/${queueId || 0}/assignment-agents`)
      .then(({ data }) => {
        setAgents(Array.isArray(data) ? data : []);
        setAgentsLoaded(true);
      })
      .catch(toastError);
  }, [open, queueId]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/prompt");
        setPrompts(data.prompts);
      } catch (err) {
        toastError(err);
      }
    })();
  }, []);

  useEffect(() => {
    api.get(`/settings`).then(({ data }) => {
      if (Array.isArray(data)) {
        const scheduleType = data.find((d) => d.key === "scheduleType");
        if (scheduleType) {
          setSchedulesEnabled(scheduleType.value === "queue");
        }

        const userRating = data.find((d) => d.key === "userRating");
        if (userRating) {
          setRatingByQueueEnabled(userRating.value === "queue");
        }
      }
    });
  }, []);

  useEffect(() => {
    setQueueSurvey(null);
    if (!open || !queueId || !ratingByQueueEnabled) return;
    api
      .get(`/satisfaction-survey/queue/${queueId}`)
      .then(({ data }) => setQueueSurvey(data))
      .catch(toastError);
  }, [open, queueId, ratingByQueueEnabled]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/queue-group");
        setQueueGroups(Array.isArray(data) ? data : []);
      } catch (err) {
        toastError(err);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/queueIntegration");

        setIntegrations(data.queueIntegrations);
      } catch (err) {
        toastError(err);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      if (!queueId) return;
      try {
        const { data } = await api.get(`/queue/${queueId}`);
        setQueue((prevState) => {
          return { ...prevState, ...data };
        });
        data.promptId ? setSelectedPrompt(data.promptId) : setSelectedPrompt(null);
        setAssignmentSettings({
          autoAssignEnabled: Boolean(data.autoAssignEnabled),
          autoAssignOffline: Boolean(data.autoAssignOffline),
          redistributeUnavailable: Boolean(data.redistributeUnavailable),
        });
        setBotMode(data.promptId ? "prompt" : data.integrationId ? "integration" : "");

        setSchedules(data.schedules);
      } catch (err) {
        toastError(err);
      }
    })();

    return () => {
      setQueue({
        name: "",
        color: "",
        greetingMessage: "",
        orderQueue: "",
        integrationId: "",
        queueGroupId: "",
        ratingEnabled: true
      });
      setSelectedPrompt(null);
      setBotMode("");
      setAssignmentSettings(DEFAULT_ASSIGNMENT_SETTINGS);
    };
  }, [queueId, open]);

  const handleClose = () => {
    onClose();
    setQueue(initialState);
  };

  const handleAttachmentFile = (e) => {
    const file = head(e.target.files);
    if (file) {
      setAttachment(file);
    }
  };

  const deleteMedia = async () => {
    if (attachment) {
      setAttachment(null);
      attachmentFile.current.value = null;
    }

    if (queue.mediaPath) {
      await api.delete(`/queue/${queue.id}/media-upload`);
      setQueue((prev) => ({ ...prev, mediaPath: null, mediaName: null }));
      toast.success(i18n.t("queueModal.toasts.deleted"));
    }
  };

  const handleSaveQueue = async (values) => {
    try {
      if (queueId) {
        await api.put(`/queue/${queueId}`, {
          ...omit(values, "ratingEnabled"),
          schedules,
          promptId: selectedPrompt ? selectedPrompt : null,
          integrationId: values.integrationId ? values.integrationId : null,
          ...assignmentPayload(),
        });
        if (attachment != null) {
          const formData = new FormData();
          formData.append("file", attachment);
          await api.post(`/queue/${queueId}/media-upload`, formData);
        }
      } else {
        await api.post("/queue", {
          ...omit(values, "ratingEnabled"),
          schedules,
          promptId: selectedPrompt ? selectedPrompt : null,
          integrationId: values.integrationId ? values.integrationId : null,
          ...assignmentPayload(),
        });
        if (attachment != null) {
          const formData = new FormData();
          formData.append("file", attachment);
          await api.post(`/queue/${queueId}/media-upload`, formData);
        }
      }
      toast.success(i18n.t("queueModal.toasts.queueSaved"));
      handleClose();
    } catch (err) {
      toastError(err);
    }
  };

  const handleApplyAssignment = () => {
    toast.success(i18n.t("queueModal.toasts.clickSaveToApply"));
    setTab(0);
  };

  const assignmentPayload = () => ({
    ...assignmentSettings,
    ...(agentsLoaded
      ? {
          members: agents
            .filter((agent) => agent.isMember)
            .map((agent) => ({ userId: agent.id, autoAssign: agent.autoAssign !== false })),
        }
      : {}),
  });

  const handleSaveSchedules = async (values) => {
    toast.success(i18n.t("queueModal.toasts.clickSaveToApply"));
    setSchedules(values);
    setTab(0);
  };

  const handleChangePrompt = (e) => {
    setSelectedPrompt(e.target.value);
  };

  return (
    <div className={classes.root}>
      {queueId && (
        <SatisfactionSurveyModal
          open={surveyModalOpen}
          queue={{ id: queueId, name: queue.name }}
          survey={queueSurvey}
          onClose={() => setSurveyModalOpen(false)}
          onSaved={setQueueSurvey}
        />
      )}
      <ConfirmationModal
        title={i18n.t("queueModal.confirmationModal.deleteTitle")}
        open={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        onConfirm={deleteMedia}
      >
        {i18n.t("queueModal.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <Dialog
        maxWidth="xl"
        fullWidth={true}
        open={open}
        onClose={handleClose}
        scroll="paper"
        PaperProps={{ className: classes.dialogPaper }}
      >
        <DialogTitle>
          {queueId
            ? `${i18n.t("queueModal.title.edit")}`
            : `${i18n.t("queueModal.title.add")}`}
          <div style={{ display: "none" }}>
            <input
              type="file"
              ref={attachmentFile}
              onChange={(e) => handleAttachmentFile(e)}
            />
          </div>
        </DialogTitle>
        <Tabs
          value={tab}
          indicatorColor="primary"
          textColor="primary"
          onChange={(_, v) => setTab(v)}
          aria-label="disabled tabs example"
        >
          <Tab value={0} label={i18n.t("queueModal.title.tabData")} />
          {schedulesEnabled && <Tab value={1} label={i18n.t("queueModal.title.tabSchedules")} />}
          <Tab value={2} label={i18n.t("queueModal.title.tabAssignment")} />
        </Tabs>
        {tab === 0 && (
          <Paper className={classes.tabPanel}>
            <Formik
              initialValues={queue}
              enableReinitialize={true}
              validationSchema={QueueSchema}
              onSubmit={(values, actions) => {
                setTimeout(() => {
                  handleSaveQueue(values);
                  actions.setSubmitting(false);
                }, 400);
              }}
            >
              {({ touched, errors, isSubmitting, values, setFieldValue }) => (
                <Form className={classes.tabForm}>
                  <DialogContent dividers>
                    <Grid container spacing={3}>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <div className={classes.formPanel}>
                          <div className={classes.sectionTitle}>{i18n.t("queueModal.sections.basics")}</div>
                          <Field
                            as={TextField}
                            label={i18n.t("queueModal.form.name")}
                            autoFocus
                            name="name"
                            error={touched.name && Boolean(errors.name)}
                            helperText={touched.name && errors.name}
                            variant="outlined"
                            size="small"
                            fullWidth
                          />
                          <div className={classes.fieldRow}>
                            <Field
                              as={TextField}
                              label={i18n.t("queueModal.form.color")}
                              name="color"
                              id="color"
                              onFocus={() => {
                                setColorPickerModalOpen(true);
                                greetingRef.current.focus();
                              }}
                              error={touched.color && Boolean(errors.color)}
                              helperText={touched.color && errors.color}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <div
                                      style={{ backgroundColor: values.color }}
                                      className={classes.colorAdorment}
                                    ></div>
                                  </InputAdornment>
                                ),
                                endAdornment: (
                                  <IconButton
                                    size="small"
                                    color="default"
                                    onClick={() => setColorPickerModalOpen(true)}
                                  >
                                    <Colorize fontSize="small" />
                                  </IconButton>
                                ),
                              }}
                              variant="outlined"
                              size="small"
                              fullWidth
                            />
                            <Field
                              as={TextField}
                              label={i18n.t("queueModal.form.orderQueue")}
                              name="orderQueue"
                              type="orderQueue"
                              error={touched.orderQueue && Boolean(errors.orderQueue)}
                              helperText={touched.orderQueue && errors.orderQueue}
                              variant="outlined"
                              size="small"
                              fullWidth
                            />
                          </div>
                          <ColorPicker
                            open={colorPickerModalOpen}
                            handleClose={() => setColorPickerModalOpen(false)}
                            onChange={(color) => {
                              values.color = color;
                              setQueue(() => {
                                return { ...values, color };
                              });
                            }}
                          />
                          <FormControl variant="outlined" size="small" fullWidth>
                            <InputLabel id="queueGroupId-selection-label">
                              {i18n.t("queueModal.form.queueGroup")}
                            </InputLabel>
                            <Field
                              as={Select}
                              label={i18n.t("queueModal.form.queueGroup")}
                              name="queueGroupId"
                              id="queueGroupId"
                              labelId="queueGroupId-selection-label"
                              value={values.queueGroupId || ""}
                            >
                              <MenuItem value={""}>{i18n.t("queueModal.form.none")}</MenuItem>
                              {queueGroups.map((grupo) => (
                                <MenuItem key={grupo.id} value={grupo.id}>
                                  {grupo.name}
                                </MenuItem>
                              ))}
                            </Field>
                          </FormControl>

                          <div className={classes.sectionTitle}>{i18n.t("queueModal.sections.bot")}</div>
                          <FormControl variant="outlined" size="small" fullWidth>
                            <InputLabel id="bot-mode-selection-label">
                              {i18n.t("queueModal.form.botMode")}
                            </InputLabel>
                            <Select
                              labelId="bot-mode-selection-label"
                              label={i18n.t("queueModal.form.botMode")}
                              value={botMode}
                              onChange={(e) => {
                                const modo = e.target.value;
                                setBotMode(modo);
                                if (modo !== "prompt") setSelectedPrompt(null);
                                if (modo !== "integration") setFieldValue("integrationId", "");
                              }}
                            >
                              <MenuItem value="">{i18n.t("queueModal.form.botModeNone")}</MenuItem>
                              <MenuItem value="prompt">{i18n.t("queueModal.form.botModePrompt")}</MenuItem>
                              <MenuItem value="integration">{i18n.t("queueModal.form.botModeIntegration")}</MenuItem>
                            </Select>
                          </FormControl>

                          {botMode === "integration" && (
                            <FormControl variant="outlined" size="small" fullWidth>
                              <InputLabel id="integrationId-selection-label">
                                {i18n.t("queueModal.form.integrationId")}
                              </InputLabel>
                              <Field
                                as={Select}
                                label={i18n.t("queueModal.form.integrationId")}
                                name="integrationId"
                                id="integrationId"
                                placeholder={i18n.t("queueModal.form.integrationId")}
                                labelId="integrationId-selection-label"
                                value={values.integrationId || ""}
                              >
                                <MenuItem value={""}>{i18n.t("queueModal.form.none")}</MenuItem>
                                {integrations.map((integration) => (
                                  <MenuItem key={integration.id} value={integration.id}>
                                    {integration.name}
                                  </MenuItem>
                                ))}
                              </Field>
                            </FormControl>
                          )}

                          {botMode === "prompt" && (
                            <FormControl variant="outlined" size="small" fullWidth>
                              <InputLabel id="dialog-select-prompt-label">
                                {i18n.t("whatsappModal.form.prompt")}
                              </InputLabel>
                              <Select
                                labelId="dialog-select-prompt-label"
                                id="dialog-select-prompt"
                                name="promptId"
                                value={selectedPrompt || ""}
                                onChange={handleChangePrompt}
                                label={i18n.t("whatsappModal.form.prompt")}
                                fullWidth
                              >
                                <MenuItem value="">{i18n.t("queueModal.form.none")}</MenuItem>
                                {promptsOfType(prompts, "text", selectedPrompt).map((prompt) => (
                                  <MenuItem key={prompt.id} value={prompt.id} disabled={!isPromptOfType(prompt, "text")}>
                                    {promptOptionLabel(prompt, "text")}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          )}

                          <div className={classes.sectionTitle}>{i18n.t("queueModal.sections.messages")}</div>
                          <Field
                            as={TextField}
                            label={i18n.t("queueModal.form.greetingMessage")}
                            type="greetingMessage"
                            multiline
                            inputRef={greetingRef}
                            minRows={4}
                            fullWidth
                            name="greetingMessage"
                            error={touched.greetingMessage && Boolean(errors.greetingMessage)}
                            helperText={touched.greetingMessage && errors.greetingMessage}
                            variant="outlined"
                            size="small"
                          />
                          {schedulesEnabled && (
                            <Field
                              as={TextField}
                              label={i18n.t("queueModal.form.outOfHoursMessage")}
                              type="outOfHoursMessage"
                              multiline
                              minRows={4}
                              fullWidth
                              name="outOfHoursMessage"
                              error={touched.outOfHoursMessage && Boolean(errors.outOfHoursMessage)}
                              helperText={touched.outOfHoursMessage && errors.outOfHoursMessage}
                              variant="outlined"
                              size="small"
                            />
                          )}
                          {queue.mediaPath || attachment ? (
                            <div className={classes.attachmentRow}>
                              <AttachFile fontSize="small" color="action" />
                              <span className={classes.attachmentName}>
                                {attachment != null ? attachment.name : queue.mediaName}
                              </span>
                              {queueEditable && (
                                <IconButton size="small" onClick={() => setConfirmationOpen(true)} color="secondary">
                                  <DeleteOutlined fontSize="small" />
                                </IconButton>
                              )}
                            </div>
                          ) : (
                            queueEditable && (
                              <Box>
                                <Button
                                  size="small"
                                  startIcon={<AttachFile fontSize="small" />}
                                  onClick={() => attachmentFile.current.click()}
                                  disabled={isSubmitting}
                                >
                                  {i18n.t("queueModal.buttons.attach")}
                                </Button>
                              </Box>
                            )
                          )}

                          {ratingByQueueEnabled && (
                            <>
                              <div className={classes.sectionTitle}>{i18n.t("queueModal.sections.survey")}</div>
                              <div className={classes.surveyRow}>
                                <Typography variant="body2" color="text.secondary">
                                  {!queueId
                                    ? i18n.t("settings.attention.survey.queueSaveFirst")
                                    : queueSurvey?.ratingEnabled
                                      ? i18n.t("settings.attention.survey.queueSends")
                                      : i18n.t("settings.attention.survey.queueNotSends")}
                                </Typography>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<ThumbsUpDownOutlined fontSize="small" />}
                                  disabled={!queueId || !queueSurvey}
                                  onClick={() => setSurveyModalOpen(true)}
                                >
                                  {i18n.t("settings.attention.survey.queueButton")}
                                </Button>
                              </div>
                            </>
                          )}
                        </div>
                      </Grid>
                      <Grid size={{ xs: 12, md: 8 }} className={classes.flowColumn}>
                        {queueId ? (
                          <QueueFlowBuilder queueId={queueId} />
                        ) : (
                          <div className={classes.flowPlaceholder}>
                            {i18n.t("queueModal.flowPlaceholder")}
                          </div>
                        )}
                      </Grid>
                    </Grid>
                  </DialogContent>
                  <DialogActions>
                    <Button
                      onClick={handleClose}
                      color="secondary"
                      disabled={isSubmitting}
                      variant="outlined"
                    >
                      {i18n.t("queueModal.buttons.cancel")}
                    </Button>
                    <Button
                      type="submit"
                      color="primary"
                      disabled={isSubmitting}
                      variant="contained"
                      className={classes.btnWrapper}
                    >
                      {queueId
                        ? `${i18n.t("queueModal.buttons.okEdit")}`
                        : `${i18n.t("queueModal.buttons.okAdd")}`}
                      {isSubmitting && (
                        <CircularProgress
                          size={24}
                          className={classes.buttonProgress}
                        />
                      )}
                    </Button>
                  </DialogActions>
                </Form>
              )}
            </Formik>
          </Paper>
        )}
        {tab === 1 && (
          <Paper className={classes.tabPanel} style={{ padding: 20 }}>
            <WeeklyScheduleTable value={schedules} onChange={setSchedules} />
            <Box textAlign="right" mt={2}>
              <Button
                variant="contained"
                color="primary"
                disabled={weekHasErrors(toWeek(schedules))}
                onClick={() => handleSaveSchedules(toWeek(schedules))}
              >
                {i18n.t("queueModal.buttons.saveSchedules")}
              </Button>
            </Box>
          </Paper>
        )}
        {tab === 2 && (
          <Paper className={classes.tabPanel}>
            <QueueAssignmentTab
              settings={assignmentSettings}
              onSettingsChange={setAssignmentSettings}
              agents={agents}
              onAgentsChange={setAgents}
              onApply={handleApplyAssignment}
            />
          </Paper>
        )}
      </Dialog>
    </div>
  );
};

export default QueueModal;
